import clsx from "clsx";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactPlayer from "react-player";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ForwardIcon,
  FullscreenExitIcon,
  FullscreenIcon,
  PauseIcon,
  PictureInPictureIcon,
  PlayIcon,
  RewindIcon,
  SeekBackwardIcon,
  SeekForwardIcon,
  VolumeOffIcon,
  VolumeOnIcon,
} from "./VideoPlayerIcons";
import styles from "./styles.module.scss";

const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2];
const SKIP_SECONDS = 10;
const SEEK_BLOCK_EPSILON = 0.25;

// react-player calls video.play() without .catch(); pause() can abort it harmlessly
if (
  typeof HTMLMediaElement !== "undefined" &&
  !HTMLMediaElement.prototype.play._abortGuarded
) {
  const originalPlay = HTMLMediaElement.prototype.play;
  HTMLMediaElement.prototype.play = function play(...args) {
    const result = originalPlay.apply(this, args);
    if (result?.catch) {
      return result.catch((err) => {
        if (err?.name === "AbortError") return;
        throw err;
      });
    }
    return result;
  };
  HTMLMediaElement.prototype.play._abortGuarded = true;
}

const formatTime = (seconds) => {
  if (Number.isNaN(seconds)) return "00:00";
  const date = new Date(seconds * 1000);
  const hh = date.getUTCHours();
  const mm = date.getUTCMinutes();
  const ss = date.getUTCSeconds().toString().padStart(2, "0");
  if (hh) return `${hh}:${mm.toString().padStart(2, "0")}:${ss}`;
  return `${mm}:${ss}`;
};

const VideoPlayer = ({
  url,
  onProgress,
  onDuration,
  onPlay,
  onPause,
  onEnded,
  onSeeked,
  onPlaylistIndexChange,
  autoPlayNext = false,
  autoPlay = true,
  playlist,
  currentPlaylistIndex,
  restrictNavigation = false,
  disableVideoSpeed = false,
  disableForwardSeek = false,
  onSeekBlocked,
  pauseSignal = 0,
  subtitleSegments = null,
}) => {
  const playerRef = useRef(null);
  const playedSecondsRef = useRef(0);
  const containerRef = useRef(null);
  const isReadyRef = useRef(false);
  const durationRef = useRef(0);
  const playingRef = useRef(false);
  const maxWatchedTimeRef = useRef(0);
  const controlsTimeoutRef = useRef(null);
  const clickTimeoutRef = useRef(null);
  const seekTimeoutRef = useRef(null);
  const volumeTimeoutRef = useRef(null);
  const playGuardTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const seekTargetRef = useRef(null);
  const isSeekingRef = useRef(false);
  const showControlsRef = useRef(true);
  const hasEndedRef = useRef(false);

  const [isReady, setIsReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [played, setPlayed] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [isIframeLoading, setIsIframeLoading] = useState(true);
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [seekOverlay, setSeekOverlay] = useState(null);
  const [seekOverlayVisible, setSeekOverlayVisible] = useState(false);
  const [navOverlay, setNavOverlay] = useState(null);
  const [navOverlayVisible, setNavOverlayVisible] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [playedSeconds, setPlayedSeconds] = useState(0);

  const volumeContainerRef = useRef(null);

  const activeSubtitleText = useMemo(() => {
    if (!Array.isArray(subtitleSegments) || !subtitleSegments.length) return null;
    const seg = subtitleSegments.find(
      (s) => playedSeconds >= s.start && playedSeconds < s.end,
    );
    return seg?.text || null;
  }, [subtitleSegments, playedSeconds]);

  const isMp4 = useMemo(
    () =>
      typeof url === "string" &&
      !url.match(/vimeo\.com\/\d+/) &&
      !url.match(/youtube\.com|youtu\.be/),
    [url],
  );

  const hasPlaylist = Array.isArray(playlist) && playlist.length > 1;
  const currentIndex = currentPlaylistIndex ?? -1;
  const hasNextVideo =
    hasPlaylist &&
    currentIndex >= 0 &&
    currentIndex < (playlist?.length ?? 0) - 1;
  const hasPreviousVideo = hasPlaylist && currentIndex > 0;
  const nextVideoName = hasNextVideo ? playlist[currentIndex + 1]?.name : null;

  const blockSeek = useCallback(() => {
    onSeekBlocked?.();
  }, [onSeekBlocked]);

  const getMediaElement = useCallback(() => {
    // react-player v3 forwards ref directly to the underlying <video> element
    const video = playerRef.current;
    return video && typeof video.currentTime === "number" ? video : null;
  }, []);

  const clearCountdown = useCallback(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    setCountdown(null);
  }, []);

  const seekToSeconds = useCallback(
    (seconds, allowBackwardOnly = false) => {
      const media = getMediaElement();
      if (!media) return;

      const currentTime = media.currentTime || 0;
      const target = Math.max(0, seconds);
      const isForward = target > currentTime + SEEK_BLOCK_EPSILON;

      if (
        restrictNavigation &&
        isForward &&
        target > maxWatchedTimeRef.current + SEEK_BLOCK_EPSILON
      ) {
        blockSeek();
        return;
      }

      media.currentTime = target;
      if (isForward && restrictNavigation) {
        maxWatchedTimeRef.current = Math.max(maxWatchedTimeRef.current, target);
      }
    },
    [blockSeek, getMediaElement, restrictNavigation],
  );

  const skipBackward = useCallback(
    (e) => {
      e?.stopPropagation();
      const media = getMediaElement();
      if (!media) return;
      seekToSeconds((media.currentTime || 0) - SKIP_SECONDS);
      setSeekOverlay({
        type: "backward",
        amount: SKIP_SECONDS,
        key: Date.now(),
      });
    },
    [getMediaElement, seekToSeconds],
  );

  const skipForward = useCallback(
    (e) => {
      e?.stopPropagation();
      if (disableForwardSeek || restrictNavigation) {
        blockSeek();
        return;
      }
      const media = getMediaElement();
      if (!media) return;
      seekToSeconds((media.currentTime || 0) + SKIP_SECONDS);
      setSeekOverlay({
        type: "forward",
        amount: SKIP_SECONDS,
        key: Date.now(),
      });
    },
    [
      blockSeek,
      disableForwardSeek,
      getMediaElement,
      restrictNavigation,
      seekToSeconds,
    ],
  );

  const handlePlayPause = useCallback((e) => {
    e?.stopPropagation();
    hasEndedRef.current = false;
    setPlaying((prev) => !prev);
    setShowSpeedMenu(false);
  }, []);

  const handleSeekMouseDown = useCallback((e) => {
    e?.stopPropagation();
    isSeekingRef.current = true;
  }, []);

  const handleSeekChange = useCallback((e) => {
    e.stopPropagation();
    setPlayed(parseFloat(e.target.value));
  }, []);

  const handleSeekMouseUp = useCallback(
    (e) => {
      e.stopPropagation();
      const value = parseFloat(e.target.value);
      const seekSeconds = value * durationRef.current;
      const media = getMediaElement();
      if (!media || !durationRef.current) {
        isSeekingRef.current = false;
        seekTargetRef.current = null;
        return;
      }

      const currentTime = media.currentTime || 0;
      const isForward = seekSeconds > currentTime + SEEK_BLOCK_EPSILON;
      if (
        restrictNavigation &&
        isForward &&
        seekSeconds > maxWatchedTimeRef.current + SEEK_BLOCK_EPSILON
      ) {
        media.currentTime = maxWatchedTimeRef.current;
        setPlayed(
          durationRef.current
            ? media.currentTime / durationRef.current
            : played,
        );
        isSeekingRef.current = false;
        seekTargetRef.current = null;
        blockSeek();
        return;
      }

      seekTargetRef.current = value;
      media.currentTime = seekSeconds;

      if (seekTimeoutRef.current) clearTimeout(seekTimeoutRef.current);
      seekTimeoutRef.current = setTimeout(() => {
        isSeekingRef.current = false;
        seekTargetRef.current = null;
        setIsIframeLoading(false);
        seekTimeoutRef.current = null;
      }, 3000);

      if (!isMp4) setIsIframeLoading(true);
    },
    [
      blockSeek,
      getMediaElement,
      isMp4,
      played,
      restrictNavigation,
    ],
  );

  const handleDurationChange = useCallback(
    (e) => {
      const dur = e.currentTarget?.duration;
      if (!Number.isFinite(dur)) return;
      setDuration(dur);
      durationRef.current = dur;
      onDuration?.(dur);
    },
    [onDuration],
  );

  const handleTimeUpdate = useCallback(
    (e) => {
      if (!isReadyRef.current) return;

      const media = e.currentTarget;
      const currentDuration = durationRef.current || media.duration || 0;
      const currentTime = media.currentTime || 0;

      if (
        restrictNavigation &&
        !isSeekingRef.current &&
        currentTime > maxWatchedTimeRef.current
      ) {
        maxWatchedTimeRef.current = currentTime;
      }

      if (
        restrictNavigation &&
        !isSeekingRef.current &&
        currentTime > maxWatchedTimeRef.current + SEEK_BLOCK_EPSILON
      ) {
        media.currentTime = maxWatchedTimeRef.current;
        blockSeek();
        return;
      }

      const playedFraction =
        currentDuration > 0 ? currentTime / currentDuration : 0;
      const progressState = {
        played: playedFraction,
        playedSeconds: currentTime,
        loaded: 0,
        loadedSeconds: 0,
      };

      playedSecondsRef.current = currentTime;
      setPlayedSeconds(currentTime);

      if (isSeekingRef.current) {
        if (seekTargetRef.current !== null && currentDuration > 0) {
          const targetSeconds = seekTargetRef.current * currentDuration;
          const diff = Math.abs(currentTime - targetSeconds);
          if (diff < 1.5) {
            isSeekingRef.current = false;
            seekTargetRef.current = null;
            if (seekTimeoutRef.current) {
              clearTimeout(seekTimeoutRef.current);
              seekTimeoutRef.current = null;
            }
            setIsIframeLoading(false);
            setPlayed(playedFraction);
            onProgress?.(progressState);
          }
        }
        return;
      }

      setPlayed(playedFraction);
      onProgress?.(progressState);
    },
    [blockSeek, onProgress, restrictNavigation],
  );

  const handleToggleFullscreen = useCallback((e) => {
    e?.stopPropagation();
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }, []);

  const handleTogglePiP = useCallback(
    (e) => {
      e?.stopPropagation();
      if (!isMp4) return;
      const media = getMediaElement();
      if (!media?.requestPictureInPicture) return;
      if (!document.pictureInPictureElement) media.requestPictureInPicture();
      else document.exitPictureInPicture?.();
    },
    [getMediaElement, isMp4],
  );

  const handleNext = useCallback(
    (e) => {
      e?.stopPropagation();
      clearCountdown();
      if (hasNextVideo) {
        setNavOverlay({ type: "next", key: Date.now() });
        onPlaylistIndexChange?.(currentIndex + 1);
      }
    },
    [clearCountdown, currentIndex, hasNextVideo, onPlaylistIndexChange],
  );

  const handlePrevious = useCallback(
    (e) => {
      e?.stopPropagation();
      clearCountdown();
      if (hasPreviousVideo) {
        setNavOverlay({ type: "previous", key: Date.now() });
        onPlaylistIndexChange?.(currentIndex - 1);
      }
    },
    [clearCountdown, currentIndex, hasPreviousVideo, onPlaylistIndexChange],
  );

  const startAutoPlayCountdown = useCallback(() => {
    if (!autoPlayNext || !hasNextVideo) return;
    clearCountdown();
    setCountdown(5);
    countdownIntervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev === null || prev <= 1) {
          clearCountdown();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [autoPlayNext, clearCountdown, hasNextVideo]);

  const handleReady = useCallback(() => {
    setIsReady(true);
    isReadyRef.current = true;
    setIsIframeLoading(false);
    setPlaying(autoPlay);
    const media = getMediaElement();
    if (media) {
      media.setAttribute?.("controlsList", "nodownload noremoteplayback");
      media.removeAttribute?.("controls");
    }
  }, [autoPlay, getMediaElement]);

  const handlePlay = useCallback(() => {
    if (!isReadyRef.current) return;
    if (playGuardTimerRef.current) clearTimeout(playGuardTimerRef.current);
    setPlaying(true);
    onPlay?.();
  }, [onPlay]);

  const handlePause = useCallback(() => {
    if (!isReadyRef.current) return;
    if (playGuardTimerRef.current) clearTimeout(playGuardTimerRef.current);
    playGuardTimerRef.current = setTimeout(() => {
      const media = getMediaElement();
      if (media?.paused) setPlaying(false);
      playGuardTimerRef.current = null;
    }, 200);
    onPause?.();
  }, [getMediaElement, onPause]);

  const handleEnded = useCallback(() => {
    hasEndedRef.current = true;
    setPlaying(false);
    setIsBuffering(false);
    onEnded?.();
    startAutoPlayCountdown();
  }, [onEnded, startAutoPlayCountdown]);

  const handleSeeked = useCallback(() => {
    setIsIframeLoading(false);
    onSeeked?.();
  }, [onSeeked]);

  useEffect(() => {
    if (countdown === 0 && hasNextVideo) {
      setCountdown(null);
      onPlaylistIndexChange?.(currentIndex + 1);
    }
  }, [countdown, currentIndex, hasNextVideo, onPlaylistIndexChange]);

  useEffect(() => {
    setPlayed(0);
    setDuration(0);
    durationRef.current = 0;
    setIsReady(false);
    isReadyRef.current = false;
    setIsIframeLoading(true);
    setPlaying(false);
    maxWatchedTimeRef.current = 0;
    clearCountdown();
  }, [url, clearCountdown]);

  useEffect(() => {
    if (pauseSignal > 0) setPlaying(false);
  }, [pauseSignal]);

  useEffect(() => {
    playingRef.current = playing;
  }, [playing]);

  useEffect(() => {
    showControlsRef.current = showControls;
  }, [showControls]);

  useEffect(() => {
    const onFullscreenChange = () =>
      setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  useEffect(() => {
    if (seekOverlay) {
      setSeekOverlayVisible(true);
      const hideTimer = setTimeout(() => setSeekOverlayVisible(false), 400);
      const removeTimer = setTimeout(() => setSeekOverlay(null), 700);
      return () => {
        clearTimeout(hideTimer);
        clearTimeout(removeTimer);
      };
    }
  }, [seekOverlay]);

  useEffect(() => {
    if (navOverlay) {
      setNavOverlayVisible(true);
      const hideTimer = setTimeout(() => setNavOverlayVisible(false), 400);
      const removeTimer = setTimeout(() => setNavOverlay(null), 700);
      return () => {
        clearTimeout(hideTimer);
        clearTimeout(removeTimer);
      };
    }
  }, [navOverlay]);

  useEffect(() => {
    const handleOutsideTouch = (e) => {
      if (
        volumeContainerRef.current &&
        !volumeContainerRef.current.contains(e.target)
      ) {
        setShowVolumeSlider(false);
      }
    };
    document.addEventListener("touchstart", handleOutsideTouch, {
      passive: true,
    });
    return () => document.removeEventListener("touchstart", handleOutsideTouch);
  }, []);

  useEffect(() => {
    const handleOutsideClick = () => setShowSpeedMenu(false);
    if (showSpeedMenu) document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, [showSpeedMenu]);

  useEffect(() => {
    const isTouchDevice = () => window.matchMedia("(pointer: coarse)").matches;
    const handleKeyDown = (e) => {
      const tag = e.target?.tagName?.toLowerCase();
      const isEditable =
        tag === "input" ||
        tag === "textarea" ||
        tag === "select" ||
        e.target?.isContentEditable;
      if (isEditable) return;

      const key = e.key.toLowerCase();
      if (
        key === " " ||
        key === "m" ||
        key === "f" ||
        key === "arrowup" ||
        key === "arrowdown"
      ) {
        e.preventDefault();
      }
      if (
        key === "arrowright" ||
        key === "arrowleft" ||
        key === "n" ||
        key === "p" ||
        key === "s"
      ) {
        e.preventDefault();
      }

      if (key === " ") setPlaying((prev) => !prev);
      if (key === "arrowright") skipForward();
      if (key === "arrowleft") skipBackward();
      if (key === "f") handleToggleFullscreen();
      if (key === "n") handleNext();
      if (key === "p") handlePrevious();
      if (key === "s" && !disableVideoSpeed) {
        setPlaybackRate((prev) => {
          const currentIndexInRates = PLAYBACK_RATES.indexOf(prev);
          const next = (currentIndexInRates + 1) % PLAYBACK_RATES.length;
          return PLAYBACK_RATES[next];
        });
      }
      if (key === "m") setMuted((prev) => !prev);
      if (key === "arrowup") {
        setShowVolumeSlider(true);
        setVolume((prev) => Math.min(prev + 0.1, 1));
      }
      if (key === "arrowdown") {
        setShowVolumeSlider(true);
        setVolume((prev) => Math.max(prev - 0.1, 0));
      }

      if (!isTouchDevice()) {
        setShowControls(true);
        if (controlsTimeoutRef.current)
          clearTimeout(controlsTimeoutRef.current);
        controlsTimeoutRef.current = setTimeout(() => {
          if (playingRef.current) setShowControls(false);
          setShowSpeedMenu(false);
        }, 3000);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    disableVideoSpeed,
    handleNext,
    handlePrevious,
    handleToggleFullscreen,
    skipBackward,
    skipForward,
  ]);

  useEffect(() => {
    return () => {
      clearCountdown();
      [
        clickTimeoutRef,
        controlsTimeoutRef,
        volumeTimeoutRef,
        seekTimeoutRef,
        playGuardTimerRef,
      ].forEach((refObj) => {
        if (refObj.current) clearTimeout(refObj.current);
      });
    };
  }, [clearCountdown]);

  const handleMouseMove = useCallback(() => {
    if (window.matchMedia("(pointer: coarse)").matches) return;
    setShowControls(true);
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    controlsTimeoutRef.current = setTimeout(() => {
      if (playingRef.current) {
        setShowControls(false);
        setShowSpeedMenu(false);
      }
    }, 3000);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (window.matchMedia("(pointer: coarse)").matches) return;
    if (playingRef.current) {
      setShowControls(false);
      setShowSpeedMenu(false);
    }
  }, []);

  const handleWrapperClick = useCallback(
    (e) => {
      if (
        window.matchMedia("(pointer: coarse)").matches &&
        !showControlsRef.current
      ) {
        setShowControls(true);
        return;
      }

      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
        clickTimeoutRef.current = null;
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return handleToggleFullscreen();
        const clickX = e.clientX - rect.left;
        if (clickX < rect.width * 0.3) skipBackward();
        else if (clickX > rect.width * 0.7) skipForward();
        else handleToggleFullscreen();
      } else {
        clickTimeoutRef.current = setTimeout(() => {
          handlePlayPause();
          setShowSpeedMenu(false);
          clickTimeoutRef.current = null;
        }, 300);
      }
    },
    [handlePlayPause, handleToggleFullscreen, skipBackward, skipForward],
  );

  return (
    <div
      dir="ltr"
      ref={containerRef}
      className={`${styles.videoContainer} ${isFullscreen ? styles.isFullscreen : ""}`}
      onContextMenu={(e) => e.preventDefault()}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      tabIndex={0}
      onClick={handleWrapperClick}
      onDoubleClick={(e) => e.preventDefault()}
    >
      <div className={styles.playerWrapper}>
        <ReactPlayer
          key={url}
          ref={playerRef}
          src={url || undefined}
          className={styles.reactPlayer}
          controls={false}
          loop={false}
          volume={volume}
          muted={muted}
          playbackRate={playbackRate}
          width="100%"
          height="100%"
          wrapper="div"
          playing={playing}
          playsInline
          config={{
            html: {
              controlsList: "nodownload",
              onContextMenu: (e) => e.preventDefault(),
            },
          }}
          onReady={handleReady}
          onTimeUpdate={handleTimeUpdate}
          onDurationChange={handleDurationChange}
          onWaiting={() => setIsBuffering(true)}
          onPlaying={() => setIsBuffering(false)}
          onPlay={handlePlay}
          onPause={handlePause}
          onEnded={handleEnded}
          onSeeked={handleSeeked}
          onError={() => {}}
        />
        {activeSubtitleText && (
          <div className={styles.subtitleOverlay} aria-live="polite">
            <span className={styles.subtitleText}>{activeSubtitleText}</span>
          </div>
        )}
      </div>

      {countdown !== null && (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center bg-black/60 rounded-[10px] p-2"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-col items-center gap-1 sm:gap-1.5 text-white max-w-full">
            {nextVideoName && (
              <p className="text-[10px] sm:text-sm font-normal text-[#B0B0BA] select-none max-w-[200px] sm:max-w-[280px] text-center truncate">
                {nextVideoName}
              </p>
            )}
            <span className="text-sm sm:text-2xl font-bold text-[#f00] tabular-nums select-none">
              {countdown}
            </span>
            <p className="text-[10px] sm:text-sm font-bold select-none">
              الفيديو التالي
            </p>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                clearCountdown();
              }}
              className="px-3 py-1 sm:px-4 sm:py-1 bg-black/30 mt-1 md:mt-2 hover:bg-[#ffffff33] rounded-lg text-[10px] sm:text-sm font-bold transition-colors duration-200"
            >
              إلغاء
            </button>
          </div>
        </div>
      )}

      {seekOverlay && (
        <div
          key={seekOverlay.key}
          className={clsx(
            "absolute inset-0 z-40 pointer-events-none flex items-center justify-between px-[10%] transition-opacity duration-300 top-[50px]",
            seekOverlayVisible ? "opacity-100" : "opacity-0",
          )}
          style={{
            justifyContent:
              seekOverlay.type === "forward" ? "flex-end" : "flex-start",
          }}
        >
          <div className="flex flex-col items-center justify-center rounded-full w-24 h-24 mb-14 text-white">
            {seekOverlay.type === "backward" ? (
              <div className="flex items-center gap-1 mt-1 text-shadow-md drop-shadow-[1px_1px_1px_black]">
                <SeekBackwardIcon size={32} />
                <span className="font-bold tabular-nums text-[11px] md:text-xl">
                  -{seekOverlay.amount}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-1 mt-1 text-shadow-md drop-shadow-[1px_1px_1px_black]">
                <span className="font-bold tabular-nums text-[11px] md:text-xl">
                  +{seekOverlay.amount}
                </span>
                <SeekForwardIcon size={32} />
              </div>
            )}
          </div>
        </div>
      )}

      {navOverlay && (
        <div
          key={navOverlay.key}
          className={clsx(
            "absolute inset-0 z-40 pointer-events-none flex items-center justify-center transition-all duration-300",
            navOverlayVisible ? "opacity-100 scale-100" : "opacity-0 scale-150",
          )}
        >
          {navOverlay.type === "previous" ? (
            <ChevronLeftIcon size={48} stroke="white" />
          ) : (
            <ChevronRightIcon size={48} stroke="white" />
          )}
        </div>
      )}

      {(hasNextVideo || hasPreviousVideo) && (
        <div
          className={clsx(
            "transition-opacity duration-300",
            showControls ? "opacity-100" : "opacity-0 pointer-events-none",
          )}
        >
          <button
            type="button"
            onClick={(e) => {
              handlePrevious();
              e.stopPropagation();
            }}
            disabled={!hasPreviousVideo}
            aria-label="الدرس السابق"
            className="size-[32px] md:size-12 grid place-items-center rounded-full absolute top-1/2 -translate-y-1/2 left-2"
          >
            <ChevronLeftIcon
              size={20}
              stroke={hasPreviousVideo ? "#f2f2f2" : "#666"}
            />
          </button>
          <button
            type="button"
            onClick={(e) => {
              handleNext();
              e.stopPropagation();
            }}
            disabled={!hasNextVideo}
            aria-label="الدرس التالي"
            className="size-[32px] md:size-12 grid place-items-center rounded-full absolute top-1/2 -translate-y-1/2 right-2"
          >
            <ChevronRightIcon
              size={20}
              stroke={hasNextVideo ? "#f2f2f2" : "#666"}
            />
          </button>
        </div>
      )}

      {isReady && (
        <div
          className={`${styles.controlsOverlay} ${!showControls && playing ? styles.hideControls : ""}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.controlsTop}>
            <input
              type="range"
              min={0}
              max={0.999999}
              step="any"
              value={played}
              onMouseDown={handleSeekMouseDown}
              onChange={handleSeekChange}
              onMouseUp={handleSeekMouseUp}
              onTouchStart={handleSeekMouseDown}
              onTouchEnd={handleSeekMouseUp}
              className={styles.seekBar}
              style={{ "--seek-before-width": `${played * 100}%` }}
            />
          </div>

          <div className={styles.controlsBottom}>
            <div className={styles.controlsLeft}>
              <button
                title="تشغيل/إيقاف"
                className={styles.controlButton}
                onClick={handlePlayPause}
              >
                {playing ? <PauseIcon /> : <PlayIcon />}
              </button>
              <button
                title="رجوع 10 ثوانٍ"
                className={`${styles.controlButton} ${!isFullscreen ? styles.hideMobile : ""}`}
                onClick={skipBackward}
              >
                <RewindIcon amount={SKIP_SECONDS} />
              </button>
              {!disableForwardSeek && !restrictNavigation && (
                <button
                  title="تقديم 10 ثوانٍ"
                  className={`${styles.controlButton} ${!isFullscreen ? styles.hideMobile : ""}`}
                  onClick={skipForward}
                >
                  <ForwardIcon amount={SKIP_SECONDS} />
                </button>
              )}
              <div
                ref={volumeContainerRef}
                className={`${styles.volumeContainer} ${showVolumeSlider ? styles.active : ""}`}
              >
                <button
                  title="كتم الصوت"
                  className={styles.controlButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    setMuted((prev) => !prev);
                  }}
                >
                  {muted || volume === 0 ? <VolumeOffIcon /> : <VolumeOnIcon />}
                </button>
                <div className={styles.volumeSliderWrap}>
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step="any"
                    value={muted ? 0 : volume}
                    onChange={(e) => {
                      const next = parseFloat(e.target.value);
                      setVolume(next);
                      setMuted(next === 0);
                    }}
                    className={styles.volumeBar}
                    style={{
                      "--volume-before-width": `${(muted ? 0 : volume) * 100}%`,
                    }}
                  />
                </div>
              </div>
              <span className={styles.timeDisplay}>
                {formatTime(played * duration)} / {formatTime(duration)}
              </span>
            </div>
            <div className={styles.controlsRight}>
              {!disableVideoSpeed && (
                <div className={styles.speedContainer}>
                  <button
                    className={styles.speedTextButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowSpeedMenu((prev) => !prev);
                    }}
                  >
                    {playbackRate}x
                  </button>
                  {showSpeedMenu && (
                    <div className={styles.speedMenu}>
                      {PLAYBACK_RATES.map((rate) => (
                        <div
                          key={rate}
                          className={`${styles.speedMenuItem} ${playbackRate === rate ? styles.active : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setPlaybackRate(rate);
                            setShowSpeedMenu(false);
                          }}
                        >
                          {rate}x
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {isMp4 && (
                <button
                  title="صورة داخل صورة"
                  className={`${styles.controlButton} ${styles.hideMobile}`}
                  onClick={handleTogglePiP}
                >
                  <PictureInPictureIcon />
                </button>
              )}
              <button
                title="ملء الشاشة"
                className={styles.controlButton}
                onClick={handleToggleFullscreen}
              >
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </button>
            </div>
          </div>
        </div>
      )}

      {((!isMp4 && isIframeLoading) ||
        (isMp4 && (!isReady || isBuffering))) && (
        <svg
          viewBox="0 0 48 48"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          data-spinner="true"
          className={styles.spinnerVideo}
        >
          <circle
            cx="24"
            cy="24"
            r="22"
            stroke="white"
            data-spinner-trace="true"
          />
          <circle
            cx="24"
            cy="24"
            r="22"
            stroke="white"
            data-spinner-circle="true"
          />
        </svg>
      )}
    </div>
  );
};

export default React.memo(VideoPlayer);
